/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogProps } from '@fluentui/react/lib/Dialog';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { SearchStrategy } from 'src/core/utils/searchStrategies';
import { AppsToEmailTable } from 'src/verticals/azureMigrationModal/newComponents/AppsToEmailTable';
import { MigrationModalFooter } from 'src/verticals/azureMigrationModal/newComponents/MigrationModalFooter';
import { AppToEmailStore } from 'src/verticals/azureMigrationModal/stores/AppToEmailStore';

export type EmailCollaboratorsScreenProps = IDialogProps & {
    appsWithCollaborators: AppToEmailStore[];
    onPrevious: () => void;
    onContinue: () => void;
};

const messages = defineMessages({
    title: {
        id: 'multiRegionMigrationModal.emailCollaboratorsScreen.title',
        defaultMessage: 'Notify collaborators'
    },
    subTitle: {
        id: 'multiRegionMigrationModal.emailCollaboratorsScreen.subTitle',
        defaultMessage: 'Once you have migrated your account to Azure, your apps will no longer be available to collaborators.'
    },
    selectCollaboratorsInstruction: {
        id: 'multiRegionMigrationModal.emailCollaboratorsScreen.selectCollaboratorsInstruction',
        defaultMessage: 'Select the collaborators you would like to notify.'
    },
    searchPlaceholder: {
        id: 'multiRegionMigrationModal.emailCollaboratorsScreen.searchPlaceholder',
        defaultMessage: 'Search by app name ...'
    },
    predicationResourceWarning: {
        id: 'multiRegionMigrationModal.emailCollaboratorsScreen.predicationResourceWarning',
        defaultMessage: `* These apps use prediction resources in a collaborator’s account. After migration,
you can continue to use the prediction resource until the owner rotates the prediction key.`
    },
    continueButton: {
        id: 'multiRegionMigrationModal.emailCollaboratorsScreen.continueButton',
        defaultMessage: 'Continue'
    },
    previousButton: {
        id: 'multiRegionMigrationModal.emailCollaboratorsScreen.previousButton',
        defaultMessage: 'Back'
    }
});

export const EmailCollaboratorsScreen = withModalMounter((props: EmailCollaboratorsScreenProps) => {
    const { appsWithCollaborators, onContinue, onPrevious } = props;
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');
    const { selection } = useSelection<AppToEmailStore>();
    const filteredApps = orderBy(SearchStrategy.substringSearchStrategy(appsWithCollaborators, query, 'appName'), ['appName'], ['desc']);
    const ownerAppsWithUnownedPredicationKeysExist = appsWithCollaborators.some(app => app.hasPredictionResource === true);

    return (
        <Stack verticalFill tokens={{ childrenGap: 40 }}>
            <Stack verticalFill tokens={{ childrenGap: 16 }}>
                <LuisText variant="xLarge" fontWeight={600}>
                    {localize(messages.title)}
                </LuisText>
                <Stack tokens={{ childrenGap: 15 }}>
                    <StackItem>{localize(messages.subTitle)}</StackItem>

                    <LuisText>{localize(messages.selectCollaboratorsInstruction)}</LuisText>

                    <LuisDebouncedSearchBox
                        size={36}
                        value={query}
                        ariaLabel={localize(messages.searchPlaceholder)}
                        placeholder={localize(messages.searchPlaceholder)}
                        onChange={(_, newValue?: string) => setQuery(newValue)}
                        debounceTime={0}
                    />

                    <AppsToEmailTable
                        data-automation-id="AppsToEmailTable"
                        items={filteredApps}
                        selection={selection}
                        areItemsFiltered={Boolean(query)}
                        overallItemsCount={filteredApps.length}
                    />
                </Stack>
                {ownerAppsWithUnownedPredicationKeysExist ? (
                    <LuisText data-automation-id="predictionKeyTextField" variant="small">
                        {localize(messages.predicationResourceWarning)}
                    </LuisText>
                ) : null}
            </Stack>

            <MigrationModalFooter backDisabled={false} nextDisabled={false} onContinue={onContinue} onPrevious={onPrevious} />
        </Stack>
    );
});
